import { type FC, useEffect, useRef, useState } from "react";

import { Info, Pencil } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";

import { PersonFieldPicker } from "@/shared/components/PersonFieldPicker";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Separator } from "@/shared/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { EstateRole } from "@/shared/constants/contactDetailOptions";
import { useAssignLegalRole } from "@/shared/hooks/useAssignLegalRole";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useWillStepAnswers } from "@/shared/hooks/useWillStepAnswers";
import {
  useWillExtraction,
  getExecutorsFromExtraction,
  findExtractionValue,
} from "@/shared/hooks/useWillExtraction";
import { toTitleCase } from "@/shared/components/document-viewer/insightUtils";
import { saveConfirmedWill } from "@/shared/utils/confirmedEstateRecords";
import {
  contactFullName,
  isNominatedExecutor,
  isProfessionalContact,
  isSuccessorExecutor,
} from "@/shared/utils/contactUtils";
import { formatPersonDateForDisplay } from "@/shared/utils/formatters";
import { sentryUtils } from "@/shared/utils/sentry";

import type { PersonContact } from "@/shared/types/estateContact";

import { LegalAuthorityStepLayout } from "./LegalAuthorityStepLayout";

interface WillField {
  label: string;
  value: string;
  key: string;
  extractionKeys: string[];
  tooltip?: string;
  person?: { filter?: (contact: PersonContact) => boolean };
}

const FIELD_DEFS: Omit<WillField, "value">[] = [
  { label: "Date signed", key: "dateSigned", extractionKeys: ["date_signed", "date_signed_raw"] },
  { label: "Where it was signed", key: "whereSigned", extractionKeys: ["state"] },
  {
    label: "Prepared by",
    key: "preparedBy",
    extractionKeys: ["prepared_by", "attorney_name", "drafting_attorney"],
    person: { filter: isProfessionalContact },
  },
  {
    label: "Named executor",
    key: "namedExecutor",
    extractionKeys: ["__executor_primary"],
    person: {},
  },
  {
    label: "Successor executor",
    key: "successorExecutor",
    extractionKeys: ["__executor_successor"],
    tooltip: "The person who steps in if the named executor can't or won't serve",
    person: {},
  },
];

interface LegalAuthorityWillConfirmStepProps {
  estateId: number;
  onBack: () => void;
  onContinue: () => void;
}

export const LegalAuthorityWillConfirmStep: FC<LegalAuthorityWillConfirmStepProps> = ({
  estateId,
  onBack,
  onContinue,
}) => {
  const { data: extraction, isFetched: extractionFetched } = useWillExtraction(estateId);
  const { savedAnswers, saveAnswers } = useWillStepAnswers(estateId);
  const { contacts, isLoading: isContactsLoading } = useUserContacts();
  const assignLegalRole = useAssignLegalRole();

  const [fields, setFields] = useState<WillField[]>(FIELD_DEFS.map((d) => ({ ...d, value: "" })));
  const [contactIds, setContactIds] = useState<Record<string, number | null>>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const extractionReady = extractionFetched && !!extraction && extraction.status === "completed";
  const hasRestored = useRef(false);

  useEffect(() => {
    if (hasRestored.current) return;
    const saved = savedAnswers?.["will-confirm"] as Record<string, unknown> | undefined;
    if (!saved) return;
    hasRestored.current = true;

    const savedFields = saved.fields as Record<string, string> | undefined;
    if (savedFields) {
      setFields((prev) =>
        prev.map((f) => ({ ...f, value: (savedFields[f.key] as string) || f.value })),
      );
    }
    setContactIds((saved.contactIds as Record<string, number | null> | undefined) ?? {});
  }, [savedAnswers]);

  useEffect(() => {
    if (!extractionReady || !extraction?.specific) return;
    if (Object.keys(extraction.specific).length === 0) return;

    const executors = getExecutorsFromExtraction(extraction.specific);

    setFields((prev) =>
      prev.map((f) => ({
        ...f,
        value:
          f.value ||
          toTitleCase(findExtractionValue(extraction.specific!, f.extractionKeys, executors)),
      })),
    );
  }, [extractionReady, extraction]);

  const handleContinue = async () => {
    setSaving(true);
    try {
      const confirmed = Object.fromEntries(fields.map((f) => [f.key, f.value]));
      await saveAnswers({ "will-confirm": { fields: confirmed, contactIds } });
      const will = await saveConfirmedWill(estateId, confirmed, contacts, contactIds).catch(
        (err) => {
          sentryUtils.captureError(err, "Saving the confirmed will record failed", { estateId });
          return null;
        },
      );
      if (will?.executorId) {
        await assignLegalRole(EstateRole.NominatedExecutor, isNominatedExecutor, [will.executorId]);
      }
      if (will?.successorId) {
        await assignLegalRole(EstateRole.SuccessorExecutor, isSuccessorExecutor, [
          will.successorId,
        ]);
      }
      onContinue();
    } catch {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (field: WillField) => {
    setEditingKey(field.key);
    setEditValue(field.value);
  };

  const saveEdit = () => {
    if (!editingKey) return;
    setFields((prev) => prev.map((f) => (f.key === editingKey ? { ...f, value: editValue } : f)));
    setEditingKey(null);
  };

  const pickPerson = (key: string, contact: PersonContact) => {
    setContactIds((prev) => ({ ...prev, [key]: contact.id }));
    setFields((prev) =>
      prev.map((f) => (f.key === key ? { ...f, value: contactFullName(contact) } : f)),
    );
    setEditingKey(null);
  };

  return (
    <LegalAuthorityStepLayout
      title="Confirm what the will says"
      description="These details come from the will. Check each item and fix anything that's off."
      activeStep={2}
      onBack={onBack}
      actionButton={
        <Button
          variant="brand"
          size="md"
          disabled={saving || isContactsLoading}
          onClick={handleContinue}
        >
          Continue
        </Button>
      }
    >
      <TooltipProvider>
        <div className="flex flex-col">
          {fields.map((field) => (
            <div key={field.key}>
              <Separator />
              <div className="flex items-center justify-between py-5">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm text-secondary">{field.label}</p>
                  {field.tooltip && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3.5 w-3.5 text-tertiary" />
                      </TooltipTrigger>
                      <TooltipContent>{field.tooltip}</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                {editingKey === field.key && field.person ? (
                  <PersonFieldPicker
                    name={field.value}
                    contactId={contactIds[field.key]}
                    filter={field.person.filter}
                    onPick={(contact) => pickPerson(field.key, contact)}
                    onCancel={() => setEditingKey(null)}
                  />
                ) : editingKey === field.key ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type={field.key === "dateSigned" ? "date" : "text"}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="h-8 w-[200px] text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit();
                        if (e.key === "Escape") setEditingKey(null);
                      }}
                    />
                    <Button variant="outline" size="sm" onClick={saveEdit}>
                      Save
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">
                      {field.key === "dateSigned" && field.value
                        ? formatPersonDateForDisplay(field.value, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                          })
                        : field.value || "-"}
                    </p>
                    <button
                      onClick={() => startEdit(field)}
                      className="text-tertiary hover:text-primary"
                      aria-label={`Edit ${field.label}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          <Separator />
        </div>
      </TooltipProvider>
    </LegalAuthorityStepLayout>
  );
};
